/**
 * Synthesis primitives for the Wild21 sound design.
 *
 * Everything the game makes noise with is generated here at runtime — there are
 * no audio assets in the build. The vocabulary is deliberately small: a
 * percussive envelope, a filtered tone, a filtered noise burst, and a generated
 * impulse response for a short, dark room.
 *
 * Signal flow, per voice:
 *
 *   source → [filter] → envelope gain → channel gain → panner ┬→ group gain ──┐
 *                                                             └→ send gain →──┤
 *                                                                 convolver →─┤
 *                                                                             ↓
 *         destination ← master gain ← SFX gain ← limiter ← compressor ← sum
 *                            ↑
 *                        music (post-limiter, see audio/index.ts)
 *
 * The two-stage compressor/limiter at the end is what lets bonuses stack (wild +
 * 21 + five-card + streak all at once) without clipping, and the per-group gains
 * let a big cue duck the card layer so the stack reads as one gesture instead of
 * mud.
 */

/** Smallest value usable with `exponentialRampToValueAtTime` (it rejects 0). */
export const FLOOR = 0.00012;

export type NoiseKind = 'white' | 'pink';

/**
 * Mix groups. Cards and UI are the constant, repetitive layer; bonuses are the
 * occasional loud layer that ducks the other two.
 */
export type MixGroup = 'card' | 'bonus' | 'ui';

export interface ChannelOptions {
  group?: MixGroup;
  /** -1 (left) .. 1 (right). */
  pan?: number;
  /** 0..1 reverb send. */
  send?: number;
}

/** A single allocated voice. Sources register themselves so the pool can steal. */
export interface Channel {
  /** Connect your envelope output here. */
  readonly input: GainNode;
  /** Hand a scheduled source to the voice so stealing can stop it. */
  own(node: AudioScheduledSourceNode): void;
  /** Push the voice's retirement time back (for tails added after allocation). */
  extend(until: number): void;
}

/** The surface every sound effect is written against. */
export interface SynthBus {
  readonly ctx: AudioContext;
  /** Context time to schedule "now" against, including a small lookahead. */
  now(): number;
  channel(when: number, until: number, opts?: ChannelOptions): Channel;
  noise(kind: NoiseKind): AudioBuffer;
  /** Momentarily pull a group down so a louder layer can sit on top of it. */
  duck(group: MixGroup, amountDb: number, hold: number): void;
}

/* ------------------------------------------------------------------ maths -- */

/** MIDI note number → frequency in Hz. A4 (69) = 440. */
export function mtof(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** Decibels → linear gain. */
export function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/** Uniform random in [min, max). */
export function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** Random detune in cents expressed as a frequency multiplier. */
export function cents(amount: number): number {
  return Math.pow(2, amount / 1200);
}

/* -------------------------------------------------------------- envelopes -- */

/**
 * Percussive attack / hold / decay envelope written straight onto an AudioParam.
 *
 * Exponential segments throughout — linear ramps on gain read as clicky and this
 * game plays the same sounds hundreds of times a session. Returns the context
 * time at which the envelope is fully closed.
 */
export function percEnv(
  param: AudioParam,
  t0: number,
  attack: number,
  hold: number,
  decay: number,
  peak: number,
): number {
  const a = Math.max(attack, 0.0008);
  const h = Math.max(hold, 0);
  const d = Math.max(decay, 0.004);
  const p = Math.max(peak, FLOOR * 4);
  param.cancelScheduledValues(t0);
  param.setValueAtTime(FLOOR, t0);
  param.exponentialRampToValueAtTime(p, t0 + a);
  if (h > 0) param.setValueAtTime(p, t0 + a + h);
  param.exponentialRampToValueAtTime(FLOOR, t0 + a + h + d);
  const end = t0 + a + h + d + 0.002;
  param.setValueAtTime(0, end);
  return end;
}

/** Exponential glide on any param, guarding against the zero-target trap. */
export function glide(
  param: AudioParam,
  t0: number,
  from: number,
  to: number,
  time: number,
): void {
  const a = Math.max(from, FLOOR);
  const b = Math.max(to, FLOOR);
  param.cancelScheduledValues(t0);
  param.setValueAtTime(a, t0);
  param.exponentialRampToValueAtTime(b, t0 + Math.max(time, 0.004));
}

/* ------------------------------------------------------------------ noise -- */

/**
 * Generate a noise buffer. Pink uses the Paul Kellet economy filter — it is
 * darker than white and far less fatiguing, which matters for the card sounds.
 */
export function createNoiseBuffer(
  ctx: BaseAudioContext,
  kind: NoiseKind,
  seconds = 2,
): AudioBuffer {
  const length = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);

  if (kind === 'white') {
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    return buffer;
  }

  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;
  for (let i = 0; i < length; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.969 * b2 + white * 0.153852;
    b3 = 0.8665 * b3 + white * 0.3104856;
    b4 = 0.55 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.016898;
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
    b6 = white * 0.115926;
  }
  return buffer;
}

/**
 * Generate a small, dark room impulse response.
 *
 * Decorrelated noise per channel through a one-pole low pass (so the tail is
 * plum-coloured rather than glassy), a short pre-delay, and a couple of early
 * reflections so it reads as a room and not as a effect.
 */
export function createImpulseResponse(
  ctx: BaseAudioContext,
  seconds = 1.5,
  decay = 3.4,
  damping = 0.26,
): AudioBuffer {
  const rate = ctx.sampleRate;
  const length = Math.max(1, Math.floor(rate * seconds));
  const buffer = ctx.createBuffer(2, length, rate);
  const preDelay = Math.floor(rate * 0.011);
  const earlies = [0.013, 0.021, 0.034, 0.047];

  for (let c = 0; c < 2; c++) {
    const data = buffer.getChannelData(c);
    let lp = 0;
    let peak = 0;
    for (let i = preDelay; i < length; i++) {
      const t = (i - preDelay) / (length - preDelay);
      const white = Math.random() * 2 - 1;
      lp += damping * (white - lp);
      const value = lp * Math.pow(1 - t, decay);
      data[i] = value;
      const abs = Math.abs(value);
      if (abs > peak) peak = abs;
    }
    for (let e = 0; e < earlies.length; e++) {
      const index = preDelay + Math.floor(rate * earlies[e]! * (c === 0 ? 1 : 1.07));
      if (index < length) data[index] = (data[index] ?? 0) + (0.5 - e * 0.1) * (c === 0 ? 1 : -1);
    }
    const norm = peak > 0 ? 0.72 / peak : 1;
    for (let i = 0; i < length; i++) data[i] = (data[i] ?? 0) * norm;
  }
  return buffer;
}

/* ------------------------------------------------------------------ waves -- */

const waveCache = new WeakMap<BaseAudioContext, Map<string, PeriodicWave>>();

/**
 * Build (and cache) a band-limited wave from a list of harmonic amplitudes.
 *
 * One oscillator with a tailored spectrum is both cheaper and more controlled
 * than stacking sines, which matters when four bonus cues land at once.
 */
export function harmonicWave(
  ctx: BaseAudioContext,
  key: string,
  partials: readonly number[],
): PeriodicWave {
  let byKey = waveCache.get(ctx);
  if (!byKey) {
    byKey = new Map();
    waveCache.set(ctx, byKey);
  }
  const cached = byKey.get(key);
  if (cached) return cached;

  const real = new Float32Array(partials.length + 1);
  const imag = new Float32Array(partials.length + 1);
  for (let i = 0; i < partials.length; i++) imag[i + 1] = partials[i]!;
  const wave = ctx.createPeriodicWave(real, imag, { disableNormalization: false });
  byKey.set(key, wave);
  return wave;
}

/* ------------------------------------------------------------------ voices -- */

export interface FilterOptions {
  type?: BiquadFilterType;
  freq: number;
  q?: number;
  /** Sweep the cutoff to this frequency over `sweepTime`. */
  sweepTo?: number;
  sweepTime?: number;
}

export interface ToneOptions extends ChannelOptions {
  freq: number;
  type?: OscillatorType;
  /** Custom spectrum from {@link harmonicWave}. Takes precedence over `type`. */
  wave?: PeriodicWave;
  /** Fine detune in cents. */
  detune?: number;
  /** Pitch glide target. */
  glideTo?: number;
  glideTime?: number;
  attack?: number;
  hold?: number;
  decay?: number;
  /** Linear peak level. */
  level?: number;
  /** Absolute context time. Defaults to `bus.now()`. */
  when?: number;
  filter?: FilterOptions;
}

/** One oscillator voice with an optional filter and pitch glide. */
export function tone(bus: SynthBus, o: ToneOptions): number {
  const ctx = bus.ctx;
  const when = o.when ?? bus.now();
  const attack = o.attack ?? 0.004;
  const hold = o.hold ?? 0;
  const decay = o.decay ?? 0.25;
  const level = o.level ?? 0.2;
  const end = when + Math.max(attack, 0.0008) + hold + Math.max(decay, 0.004) + 0.01;

  const channel = bus.channel(when, end, {
    group: o.group,
    pan: o.pan,
    send: o.send,
  });
  channel.input.gain.value = level;

  const osc = ctx.createOscillator();
  if (o.wave) osc.setPeriodicWave(o.wave);
  else osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(Math.max(o.freq, 1), when);
  if (o.detune) osc.detune.setValueAtTime(o.detune, when);
  if (o.glideTo !== undefined) {
    glide(osc.frequency, when, o.freq, o.glideTo, o.glideTime ?? decay * 0.7);
  }

  const env = ctx.createGain();
  env.gain.value = 0;
  percEnv(env.gain, when, attack, hold, decay, 1);

  let head: AudioNode = osc;
  if (o.filter) head = applyFilter(ctx, head, o.filter, when);
  head.connect(env);
  env.connect(channel.input);

  osc.start(when);
  osc.stop(end);
  channel.own(osc);
  return end;
}

export interface NoiseOptions extends ChannelOptions {
  kind?: NoiseKind;
  attack?: number;
  hold?: number;
  decay?: number;
  level?: number;
  when?: number;
  /** Resamples the noise; a cheap way to shift its perceived weight. */
  rate?: number;
  filter?: FilterOptions;
  /** Optional second filter, e.g. a high pass under a band pass. */
  filter2?: FilterOptions;
}

/** One filtered noise burst. The backbone of every card sound. */
export function noiseBurst(bus: SynthBus, o: NoiseOptions): number {
  const ctx = bus.ctx;
  const when = o.when ?? bus.now();
  const attack = o.attack ?? 0.002;
  const hold = o.hold ?? 0;
  const decay = o.decay ?? 0.1;
  const level = o.level ?? 0.1;
  const end = when + Math.max(attack, 0.0008) + hold + Math.max(decay, 0.004) + 0.01;

  const channel = bus.channel(when, end, {
    group: o.group,
    pan: o.pan,
    send: o.send,
  });
  channel.input.gain.value = level;

  const buffer = bus.noise(o.kind ?? 'pink');
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.loop = true;
  if (o.rate !== undefined) src.playbackRate.setValueAtTime(Math.max(o.rate, 0.05), when);

  const env = ctx.createGain();
  env.gain.value = 0;
  percEnv(env.gain, when, attack, hold, decay, 1);

  let head: AudioNode = src;
  if (o.filter) head = applyFilter(ctx, head, o.filter, when);
  if (o.filter2) head = applyFilter(ctx, head, o.filter2, when);
  head.connect(env);
  env.connect(channel.input);

  // Random start offset so repeated bursts never sample the same noise.
  const offset = Math.random() * Math.max(buffer.duration - 0.25, 0.01);
  src.start(when, offset);
  src.stop(end);
  channel.own(src);
  return end;
}

function applyFilter(
  ctx: AudioContext,
  source: AudioNode,
  o: FilterOptions,
  when: number,
): AudioNode {
  const filter = ctx.createBiquadFilter();
  filter.type = o.type ?? 'lowpass';
  filter.frequency.setValueAtTime(clamp(o.freq, 20, 20000), when);
  filter.Q.setValueAtTime(o.q ?? 0.7, when);
  if (o.sweepTo !== undefined) {
    glide(filter.frequency, when, clamp(o.freq, 20, 20000), clamp(o.sweepTo, 20, 20000), o.sweepTime ?? 0.12);
  }
  source.connect(filter);
  return filter;
}

/* ------------------------------------------------------------------ engine -- */

interface VoiceNodes {
  input: GainNode;
  panner: StereoPannerNode;
  send: GainNode | null;
}

class PoolVoice implements Channel {
  readonly input: GainNode;
  readonly startAt: number;
  endAt: number;
  #nodes: VoiceNodes;
  #sources: AudioScheduledSourceNode[] = [];
  #dead = false;
  #ctx: AudioContext;

  constructor(ctx: AudioContext, nodes: VoiceNodes, startAt: number, endAt: number) {
    this.#ctx = ctx;
    this.#nodes = nodes;
    this.input = nodes.input;
    this.startAt = startAt;
    this.endAt = endAt;
  }

  get dead(): boolean {
    return this.#dead;
  }

  own(node: AudioScheduledSourceNode): void {
    this.#sources.push(node);
  }

  extend(until: number): void {
    if (until > this.endAt) this.endAt = until;
  }

  /** Fade out fast and free the nodes. Used both for retirement and stealing. */
  kill(fade: number): void {
    if (this.#dead) return;
    this.#dead = true;
    const now = this.#ctx.currentTime;
    const stopAt = now + fade;
    if (fade > 0) {
      const gain = this.input.gain;
      const current = Math.max(gain.value, FLOOR);
      gain.cancelScheduledValues(now);
      gain.setValueAtTime(current, now);
      gain.linearRampToValueAtTime(0, stopAt);
    }
    for (const source of this.#sources) {
      try {
        source.stop(stopAt);
      } catch {
        /* already stopped — harmless */
      }
    }
    this.#sources.length = 0;
    const disconnectAt = Math.max(0, (stopAt - now) * 1000 + 40);
    globalThis.setTimeout(() => this.#disconnect(), disconnectAt);
  }

  #disconnect(): void {
    this.#nodes.input.disconnect();
    this.#nodes.panner.disconnect();
    this.#nodes.send?.disconnect();
  }
}

export interface SynthEngineOptions {
  maxVoices?: number;
  reverbSeconds?: number;
  /** Scheduling lookahead so envelopes never start in the past. */
  lookahead?: number;
}

const GROUP_BASE: Record<MixGroup, number> = {
  card: 1,
  bonus: 0.95,
  ui: 0.85,
};

/**
 * Owns the audio graph: mix groups, the shared reverb, the limiter chain and the
 * voice pool. Sound effects only ever see it through {@link SynthBus}.
 */
export class SynthEngine implements SynthBus {
  readonly ctx: AudioContext;
  readonly master: GainNode;
  /**
   * Everything this engine makes, before the master fader.
   *
   * The music beds join the graph at `master`, post-limiter, so that a bonus
   * stack cannot pump them. That is the right place for them and it has one
   * consequence: `master` is the only fader both layers pass through, so
   * turning it down turns down the music too. A player who wants the bed
   * without the card sounds has nothing to reach for.
   *
   * This node is that thing. It sits after the limiter so muting the effects
   * cannot change how the effects that are still playing are compressed, and
   * before `master` so the global mute still silences everything.
   */
  readonly sfx: GainNode;

  #groups: Record<MixGroup, GainNode>;
  #sum: GainNode;
  #convolver: ConvolverNode;
  #reverbReturn: GainNode;
  #noise = new Map<NoiseKind, AudioBuffer>();
  #voices: PoolVoice[] = [];
  #maxVoices: number;
  #lookahead: number;
  #disposed = false;

  constructor(ctx: AudioContext, options: SynthEngineOptions = {}) {
    this.ctx = ctx;
    this.#maxVoices = Math.max(4, options.maxVoices ?? 28);
    this.#lookahead = options.lookahead ?? 0.012;

    this.master = ctx.createGain();
    this.master.gain.value = 1;
    this.sfx = ctx.createGain();
    this.sfx.gain.value = 1;

    // Two-stage dynamics: a musical glue compressor, then a fast brick-ish
    // limiter so a four-way bonus stack lands loud but never clips.
    //
    // Note: Blink's DynamicsCompressorNode starts fully reduced and recovers
    // over roughly one release period once the context is actually rendering,
    // so the first cue within ~250 ms of a resume is a few dB quiet. Harmless,
    // and it self-corrects; do not "fix" it by shortening the release.
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -9;
    compressor.knee.value = 6;
    compressor.ratio.value = 3;
    compressor.attack.value = 0.006;
    compressor.release.value = 0.18;

    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -1.5;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.06;

    // Keep sub rumble out of the limiter so it never pumps.
    const rumble = ctx.createBiquadFilter();
    rumble.type = 'highpass';
    rumble.frequency.value = 42;
    rumble.Q.value = 0.6;

    this.#sum = ctx.createGain();
    this.#sum.gain.value = 1;
    this.#sum.connect(rumble);
    rumble.connect(compressor);
    compressor.connect(limiter);
    limiter.connect(this.sfx);
    this.sfx.connect(this.master);
    this.master.connect(ctx.destination);

    this.#convolver = ctx.createConvolver();
    this.#convolver.normalize = true;
    this.#convolver.buffer = createImpulseResponse(ctx, options.reverbSeconds ?? 1.5);
    this.#reverbReturn = ctx.createGain();
    this.#reverbReturn.gain.value = 0.85;
    this.#convolver.connect(this.#reverbReturn);
    this.#reverbReturn.connect(this.#sum);

    const makeGroup = (base: number): GainNode => {
      const g = ctx.createGain();
      g.gain.value = base;
      g.connect(this.#sum);
      return g;
    };
    this.#groups = {
      card: makeGroup(GROUP_BASE.card),
      bonus: makeGroup(GROUP_BASE.bonus),
      ui: makeGroup(GROUP_BASE.ui),
    };
  }

  now(): number {
    return this.ctx.currentTime + this.#lookahead;
  }

  get voiceCount(): number {
    return this.#voices.length;
  }

  noise(kind: NoiseKind): AudioBuffer {
    let buffer = this.#noise.get(kind);
    if (!buffer) {
      buffer = createNoiseBuffer(this.ctx, kind, 2);
      this.#noise.set(kind, buffer);
    }
    return buffer;
  }

  channel(when: number, until: number, opts: ChannelOptions = {}): Channel {
    this.#prune();
    if (this.#voices.length >= this.#maxVoices) this.#steal();

    const ctx = this.ctx;
    const input = ctx.createGain();
    const panner = ctx.createStereoPanner();
    panner.pan.setValueAtTime(clamp(opts.pan ?? 0, -1, 1), Math.max(when, ctx.currentTime));
    input.connect(panner);
    panner.connect(this.#groups[opts.group ?? 'card']);

    let send: GainNode | null = null;
    const amount = opts.send ?? 0;
    if (amount > 0) {
      send = ctx.createGain();
      send.gain.value = clamp(amount, 0, 1);
      panner.connect(send);
      send.connect(this.#convolver);
    }

    const voice = new PoolVoice(ctx, { input, panner, send }, when, until + 0.06);
    this.#voices.push(voice);
    const lifetime = Math.max(0, (voice.endAt - ctx.currentTime) * 1000 + 80);
    globalThis.setTimeout(() => {
      if (!voice.dead) voice.kill(0);
      this.#remove(voice);
    }, lifetime);
    return voice;
  }

  duck(group: MixGroup, amountDb: number, hold: number): void {
    const node = this.#groups[group];
    const base = GROUP_BASE[group];
    const target = base * dbToGain(-Math.abs(amountDb));
    const t = this.ctx.currentTime;
    const gain = node.gain;
    gain.cancelScheduledValues(t);
    gain.setValueAtTime(gain.value, t);
    gain.linearRampToValueAtTime(target, t + 0.035);
    gain.setValueAtTime(target, t + 0.035 + Math.max(hold, 0));
    gain.linearRampToValueAtTime(base, t + 0.035 + Math.max(hold, 0) + 0.4);
  }

  /** Ramp the master gain (used for mute and volume). */
  setMasterGain(value: number, ramp = 0.06): void {
    this.#ramp(this.master.gain, value, ramp);
  }

  /**
   * Ramp the effects bus, independent of the master and of the music.
   *
   * Ramped rather than set, like everything else on this graph: a gain step on
   * a bus with voices sounding through it is a click, and the one moment a
   * player changes this setting is the one moment they are listening for it.
   */
  setSfxGain(value: number, ramp = 0.06): void {
    this.#ramp(this.sfx.gain, value, ramp);
  }

  #ramp(gain: AudioParam, value: number, ramp: number): void {
    const t = this.ctx.currentTime;
    gain.cancelScheduledValues(t);
    gain.setValueAtTime(gain.value, t);
    gain.linearRampToValueAtTime(clamp(value, 0, 1.5), t + Math.max(ramp, 0.001));
  }

  /** Cut every sounding voice — used when the tab is hidden or on dispose. */
  panic(): void {
    for (const voice of this.#voices) voice.kill(0.02);
    this.#voices.length = 0;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.panic();
    this.sfx.disconnect();
    this.master.disconnect();
    this.#sum.disconnect();
    this.#convolver.disconnect();
    this.#reverbReturn.disconnect();
    for (const group of Object.values(this.#groups)) group.disconnect();
    void this.ctx.close().catch(() => undefined);
  }

  #prune(): void {
    const now = this.ctx.currentTime;
    for (let i = this.#voices.length - 1; i >= 0; i--) {
      const voice = this.#voices[i]!;
      if (voice.dead || voice.endAt <= now) {
        if (!voice.dead) voice.kill(0);
        this.#voices.splice(i, 1);
      }
    }
  }

  /** Voice limiting: the oldest sounding voice gives way. */
  #steal(): void {
    let oldest: PoolVoice | null = null;
    for (const voice of this.#voices) {
      if (!oldest || voice.startAt < oldest.startAt) oldest = voice;
    }
    if (!oldest) return;
    oldest.kill(0.025);
    this.#remove(oldest);
  }

  #remove(voice: PoolVoice): void {
    const index = this.#voices.indexOf(voice);
    if (index >= 0) this.#voices.splice(index, 1);
  }
}
